import { useState, useEffect, useCallback } from 'react';
import P2PService from '../services/p2pService';
import { toast } from 'react-toastify';

// Ads and orders use the same paginated API response.
const fetchAllP2PPages = async (fetchPage, filters = {}) => {
  let data = await fetchPage(filters);
  const results = Array.isArray(data) ? data : [...(data?.results || [])];
  const visited = new Set();
  while (data?.next) {
    const query = new URL(data.next, window.location.origin).search;
    if (visited.has(query)) throw new Error('Repeated P2P page');
    visited.add(query);
    data = await fetchPage({ ...filters, ...Object.fromEntries(new URLSearchParams(query)) });
    results.push(...(Array.isArray(data) ? data : data?.results || []));
  }
  return results;
};

// Hook for P2P profile management
export const useP2PProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.getProfile();
      setProfile(data);
    } catch (err) {
      setError(err.message);
      if (err.response?.status === 404) {
        // Profile doesn't exist, user needs to create one
        setProfile(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const createProfile = useCallback(async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.createProfile(profileData);
      setProfile(data);
      toast.success('P2P profile created successfully!');
      return data;
    } catch (err) {
      setError(err.message);
      toast.error('Failed to create P2P profile');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.updateProfile(profileData);
      setProfile(data);
      toast.success('Profile updated successfully!');
      return data;
    } catch (err) {
      setError(err.message);
      toast.error('Failed to update profile');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    createProfile,
    updateProfile,
    refetch: fetchProfile
  };
};

// Hook for P2P advertisements
export const useP2PAds = (filters = {}) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAds = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllP2PPages(params => P2PService.getAds(params), { ...filters, ...customFilters });
      setAds(data);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch advertisements');
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  return {
    ads,
    loading,
    error,
    refetch: fetchAds
  };
};

// Hook for user's own ads
export const useMyAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMyAds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllP2PPages(params => P2PService.getMyAds(params));
      setAds(data);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch your advertisements');
    } finally {
      setLoading(false);
    }
  }, []);

  const createAd = useCallback(async (adData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.createAd(adData);
      setAds(prev => [data, ...prev]);
      toast.success('Advertisement created successfully!');
      return data;
    } catch (err) {
      setError(err.message);
      toast.error('Failed to create advertisement');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAd = useCallback(async (adId, adData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.updateAd(adId, adData);
      setAds(prev => prev.map(ad => ad.ad_id === adId ? data : ad));
      toast.success('Advertisement updated successfully!');
      return data;
    } catch (err) {
      setError(err.message);
      toast.error('Failed to update advertisement');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAd = useCallback(async (adId) => {
    setLoading(true);
    setError(null);
    try {
      await P2PService.deleteAd(adId);
      setAds(prev => prev.filter(ad => ad.ad_id !== adId));
      toast.success('Advertisement deleted successfully!');
    } catch (err) {
      setError(err.message);
      toast.error('Failed to delete advertisement');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyAds();
  }, [fetchMyAds]);

  return {
    ads,
    loading,
    error,
    createAd,
    updateAd,
    deleteAd,
    refetch: fetchMyAds
  };
};

// Hook for P2P orders
export const useP2POrders = (filters = {}) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOrders = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllP2PPages(params => P2PService.getOrders(params), { ...filters, ...customFilters });
      setOrders(data);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]);

  const createOrder = useCallback(async (orderData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.createOrder(orderData);
      setOrders(prev => [data, ...prev]);
      toast.success('Order created successfully!');
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create order';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    createOrder,
    refetch: fetchOrders
  };
};

// Hook for P2P order detail with WebSocket
export const useP2POrder = (orderId) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [socket, setSocket] = useState(null);

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.getOrderDetail(orderId);
      setOrder(data);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // WebSocket connection
  useEffect(() => {
    if (!orderId) return;

    const ws = P2PService.connectOrderWebSocket(orderId, {
      onMessage: (data) => {
        if (data.type === 'order_data') {
          setOrder(data.data);
        } else if (data.type === 'order_status_update') {
          setOrder(prev => ({ ...prev, ...data.data }));
        }
      },
      onError: (error) => {
        console.error('WebSocket error:', error);
      }
    });

    setSocket(ws);

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [orderId]);

  // Order actions
  const markPaid = useCallback(async (paymentReference) => {
    try {
      const data = await P2PService.markOrderPaid(orderId, paymentReference);
      setOrder(data);
      toast.success('Order marked as paid!');
      return data;
    } catch (err) {
      toast.error('Failed to mark order as paid');
      throw err;
    }
  }, [orderId]);

  const confirmPayment = useCallback(async () => {
    try {
      const data = await P2PService.confirmPayment(orderId);
      setOrder(data);
      toast.success('Payment confirmed!');
      return data;
    } catch (err) {
      toast.error('Failed to confirm payment');
      throw err;
    }
  }, [orderId]);

  const releaseCrypto = useCallback(async () => {
    try {
      const data = await P2PService.releaseCrypto(orderId);
      setOrder(data);
      toast.success('Crypto released successfully!');
      return data;
    } catch (err) {
      toast.error('Failed to release crypto');
      throw err;
    }
  }, [orderId]);

  const cancelOrder = useCallback(async (reason) => {
    try {
      const data = await P2PService.cancelOrder(orderId, reason);
      setOrder(data);
      toast.success('Order cancelled');
      return data;
    } catch (err) {
      toast.error('Failed to cancel order');
      throw err;
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    order,
    loading,
    error,
    socket,
    markPaid,
    confirmPayment,
    releaseCrypto,
    cancelOrder,
    refetch: fetchOrder
  };
};

// Hook for P2P chat
export const useP2PChat = (orderId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMessages = useCallback(async () => {
    if (!orderId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.getChatMessages(orderId);
      setMessages(data.results || data);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch chat messages');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const sendMessage = useCallback(async (messageData) => {
    try {
      const data = await P2PService.sendChatMessage(orderId, messageData);
      setMessages(prev => [...prev, data]);
      return data;
    } catch (err) {
      toast.error('Failed to send message');
      throw err;
    }
  }, [orderId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    messages,
    loading,
    error,
    sendMessage,
    refetch: fetchMessages
  };
};

// Hook for P2P statistics
export const useP2PStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.getStats();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    
    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  };
};

// Hook for payment methods
export const usePaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPaymentMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await P2PService.getPaymentMethods();
      setPaymentMethods(data.results || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  return {
    paymentMethods,
    loading,
    error,
    refetch: fetchPaymentMethods
  };
};
